import { useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, NotePencil, PauseCircle, FastForward, DotsThreeVertical, ArrowUUpLeft } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ReturnLaterPanel } from "@/components/ReturnLater";

export const TRIAGE_ACTIONS = [
  { key: "done",        label: "Already Done", icon: CheckCircle,  cls: "text-emerald-700 hover:bg-emerald-50", note: true },
  { key: "needs_notes", label: "Needs Notes",  icon: NotePencil,   cls: "text-orange-700 hover:bg-orange-50", note: true },
  { key: "hold",        label: "Hold / Not Now", icon: PauseCircle, cls: "text-slate-700 hover:bg-slate-100" },
  { key: "skip",        label: "Skip 7 Days",  icon: FastForward,  cls: "text-sky-700 hover:bg-sky-50", sweeperOnly: true },
];

export async function runTriage(jobIds, action, { askNote } = {}) {
  let note = "";
  if (askNote) {
    note = window.prompt(`${action.label} — add a quick note (optional):`, "") ?? null;
    if (note === null) return null;
  }
  const r = await api.post("/jobs/triage", { job_ids: jobIds, action: action.key, note });
  toast.success(`${r.data.updated} job${r.data.updated === 1 ? "" : "s"} → ${action.label}`);
  return r.data;
}

// Inline ⋮ menu for one job. onDone(actionKey) fires after the API call succeeds.
export function TriageMenu({ jobId, onDone, sweeper = false, open: forcedOpen, onClose }) {
  const [open, setOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const isOpen = forcedOpen ?? open;
  const close = () => { setOpen(false); setReturnOpen(false); onClose?.(); };
  const actions = TRIAGE_ACTIONS.filter(a => sweeper || !a.sweeperOnly);
  const fire = async (a) => {
    try {
      const res = await runTriage([jobId], a, { askNote: a.note });
      if (res) onDone?.(a.key);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Triage failed")); }
    close();
  };
  return (
    <div className="relative shrink-0" onClick={e => e.stopPropagation()}>
      {forcedOpen === undefined && (
        <button onClick={() => setOpen(o => !o)} data-testid={`triage-btn-${jobId}`}
          className="h-6 w-6 flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded">
          <DotsThreeVertical weight="bold" size={16}/>
        </button>
      )}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={close}/>
          {returnOpen ? (
            <div className="absolute right-0 top-7 z-40">
              <ReturnLaterPanel jobId={jobId} compact onClose={close} onDone={() => onDone?.("return_later")}/>
            </div>
          ) : (
          <div className="absolute right-0 top-7 z-40 w-44 bg-white border-2 border-slate-900 shadow-[3px_3px_0_#0f172a] py-1" data-testid={`triage-menu-${jobId}`}>
            {actions.map(a => (
              <button key={a.key} onClick={() => fire(a)} data-testid={`triage-${a.key}-${jobId}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-[11px] font-black uppercase tracking-wider text-left ${a.cls}`}>
                <a.icon weight="fill" size={14}/> {a.label}
              </button>
            ))}
            <button onClick={() => setReturnOpen(true)} data-testid={`triage-return_later-${jobId}`}
              className="w-full flex items-center gap-2 px-3 py-2 text-[11px] font-black uppercase tracking-wider text-left text-orange-700 hover:bg-orange-50">
              <ArrowUUpLeft weight="bold" size={14}/> Return another day
            </button>
          </div>
          )}
        </>
      )}
    </div>
  );
}

// Touch swipe-left detector. Wrap a card; calls onSwipe() after a 60px left swipe.
export function useSwipeLeft(onSwipe) {
  const start = useRef(null);
  return {
    onTouchStart: (e) => { start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; },
    onTouchEnd: (e) => {
      if (!start.current) return;
      const dx = e.changedTouches[0].clientX - start.current.x;
      const dy = Math.abs(e.changedTouches[0].clientY - start.current.y);
      start.current = null;
      if (dx < -60 && dy < 40) onSwipe();
    },
  };
}
